import { useState } from "react";
import { MessageCircle, CalendarDays, Users, Sparkles, MapPin } from "lucide-react";
import useSiteConfig, { whatsappLinkFromCfg } from "@/lib/useSiteConfig";

/**
 * FinalCTAWidget — low-friction "Talk to us on WhatsApp" widget.
 *
 * Replaces the long-form lead form on the home page bottom: the visitor
 * picks dates, guests, purpose and a preferred location, and we deep-link
 * them straight into WhatsApp with a pre-filled message that contains
 * everything we need to respond intelligently. No backend round-trip,
 * no form abandonment.
 *
 * The destination WhatsApp number is pulled from /api/site-config so the
 * admin can change it once and every CTA on the site picks it up.
 */
const PURPOSES = [
  "Couple stay",
  "Family stay",
  "Private gathering",
  "Shoot / content creation",
  "Workation / long stay",
  "Other",
];

const LOCATIONS = [
  "Near Dhan Mill",
  "Greater Kailash",
  "Uppal Southend",
  "Gurugram",
  "South Delhi",
  "Not sure yet",
];

const plusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
};

const addDays = (iso, n) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
};

export default function FinalCTAWidget() {
  const cfg = useSiteConfig();
  const [checkIn, setCheckIn] = useState(plusDays(7));
  const [checkOut, setCheckOut] = useState(plusDays(9));
  const [guests, setGuests] = useState(2);
  const [purpose, setPurpose] = useState(PURPOSES[0]);
  const [loc, setLoc] = useState(LOCATIONS[0]);

  // Calendar contract: check-out must be strictly after check-in. Snap on
  // change so the pre-filled WhatsApp message can never carry an invalid
  // range.
  const onCheckInChange = (v) => {
    setCheckIn(v);
    if (v && (!checkOut || new Date(checkOut) <= new Date(v))) {
      setCheckOut(addDays(v, 1));
    }
  };

  const today = new Date().toISOString().split("T")[0];
  const checkOutMin = checkIn ? addDays(checkIn, 1) : today;

  const buildMessage = () => `Hi Vyagram, I would like to check availability for a stay.

Check-in: ${checkIn}
Check-out: ${checkOut}
Guests: ${guests}
Purpose: ${purpose}
Preferred location: ${loc}

Please help me with suitable options.`;

  const waHref = whatsappLinkFromCfg(cfg, buildMessage());

  return (
    <section
      data-testid="final-cta-widget"
      className="vyg-section bg-gradient-to-br from-forest to-[#0F3027] text-parchment"
    >
      <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-[1.05fr_1fr] lg:gap-16 lg:px-10">
        <div>
          <p className="vyg-eyebrow !text-gold">Plan in a minute</p>
          {/*
            Force the heading + body copy to parchment. The site-wide
            `font-display` resets the colour to noir (dark) which is
            unreadable against the forest-green background of this section.
            We override here so the editorial heading and body always sit
            on the dark band with strong contrast.
          */}
          <h2 className="mt-3 font-display text-4xl text-parchment sm:text-5xl">
            Tell us a little, <span className="font-script text-gold">we'll take it from there.</span>
          </h2>
          <p className="editorial-body mt-5 max-w-xl !text-base !text-parchment/90">
            No long forms. Pick your dates, share what kind of stay you're planning, and we'll
            come back on WhatsApp with the right home — and the right price — within minutes.
          </p>

          <div className="mt-8 hidden grid-cols-2 gap-4 text-[13px] !text-parchment/90 sm:grid sm:max-w-md">
            <div className="rounded-xl border border-parchment/20 bg-white/5 p-4">
              <p className="font-display text-2xl text-gold">Direct rates</p>
              <p className="mt-1 text-xs text-parchment/85">Skip OTA markups. Same homes, lower price.</p>
            </div>
            <div className="rounded-xl border border-parchment/20 bg-white/5 p-4">
              <p className="font-display text-2xl text-gold">On-ground team</p>
              <p className="mt-1 text-xs text-parchment/85">Hosts, housekeeping and concierge — all ours.</p>
            </div>
          </div>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); window.open(waHref, "_blank", "noopener"); }}
          className="rounded-2xl border border-parchment/15 bg-parchment/95 p-5 text-noir shadow-card sm:p-6"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Check in" icon={CalendarDays}>
              <input
                type="date"
                value={checkIn}
                min={today}
                onChange={(e) => onCheckInChange(e.target.value)}
                data-testid="final-cta-checkin"
                className="w-full bg-transparent text-sm font-medium focus:outline-none"
              />
            </Field>
            <Field label="Check out" icon={CalendarDays}>
              <input
                type="date"
                value={checkOut}
                min={checkOutMin}
                onChange={(e) => setCheckOut(e.target.value)}
                data-testid="final-cta-checkout"
                className="w-full bg-transparent text-sm font-medium focus:outline-none"
              />
            </Field>
            <Field label="Guests" icon={Users}>
              <select
                value={guests}
                onChange={(e) => setGuests(Number(e.target.value))}
                data-testid="final-cta-guests"
                className="w-full bg-transparent text-sm font-medium focus:outline-none"
              >
                {Array.from({ length: 16 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? "guest" : "guests"}</option>
                ))}
              </select>
            </Field>
            <Field label="Purpose of stay" icon={Sparkles}>
              <select
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                data-testid="final-cta-purpose"
                className="w-full bg-transparent text-sm font-medium focus:outline-none"
              >
                {PURPOSES.map((p) => (<option key={p} value={p}>{p}</option>))}
              </select>
            </Field>
            <Field label="Preferred location" icon={MapPin} className="sm:col-span-2">
              <select
                value={loc}
                onChange={(e) => setLoc(e.target.value)}
                data-testid="final-cta-location"
                className="w-full bg-transparent text-sm font-medium focus:outline-none"
              >
                {LOCATIONS.map((l) => (<option key={l} value={l}>{l}</option>))}
              </select>
            </Field>
          </div>
          <button
            type="submit"
            data-testid="final-cta-submit"
            data-href={waHref}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3.5 text-sm font-medium text-white transition-transform hover:-translate-y-0.5"
          >
            <MessageCircle size={16} /> Talk to us on WhatsApp
          </button>
          <p className="mt-3 text-center text-[11px] text-noir/60">
            Opens a chat with our hospitality team — no form submission, no spam.
          </p>
        </form>
      </div>
    </section>
  );
}

function Field({ label, icon: Icon, children, className = "" }) {
  return (
    <label className={`block rounded-xl border border-forest/15 bg-white px-3 py-2 ${className}`}>
      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber">
        <Icon size={12} /> {label}
      </span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}
